import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UpdateUserRequest, User, UserBase } from "@front/m19-api-client";
import { AuthService } from "@front/m19-services";
import { IIconComponent, RegisterFormComponent, RegisterFormValue, ToasterService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { LayoutSelectorService } from "@m19-board/layout-selector.service";
import { take, throwError } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Component({
  selector: "app-register-invite",
  imports: [RegisterFormComponent, TranslocoDirective, IIconComponent],
  templateUrl: "./register-invite.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterInviteComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly layoutSelector = inject(LayoutSelectorService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  termsAndConditionLink = this.layoutSelector.getTermsAndConditionLink();
  agency = this.layoutSelector.getAgencyName();

  fetchingUser = signal(false);

  readonly registerLoading = signal(false);
  readonly user = signal<User | null>(null);
  readonly tokenExpired = signal(false);
  formValue = signal<RegisterFormValue | null>(null);

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get("token");
    if (!token) {
      this.tokenExpired.set(true);
      return;
    }
    this.authService.setAccessToken(token, 3600, false);
    this.fetchingUser.set(true);
    this.authService
      .isLoggedIn()
      .pipe(
        switchMap((loggedIn) => {
          if (!loggedIn) {
            this.tokenExpired.set(true);
            return throwError(() => {});
          }
          return this.authService.loggedUser$;
        }),
        take(1),
      )
      .subscribe({
        next: (user) => {
          if (!user) {
            this.tokenExpired.set(true);
            this.fetchingUser.set(false);
            return;
          }
          this.user.set({ ...user });
          if (user.registered) {
            this.router.navigateByUrl("login");
          }
          this.fetchingUser.set(false);
        },
        error: () => {
          this.user.set(null);
          this.tokenExpired.set(true);
          this.fetchingUser.set(false);
        },
      });
  }

  registerInvite(formValue: RegisterFormValue): void {
    const updateInviteUser: UpdateUserRequest = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      userName: formValue.firstName + " " + formValue.lastName,
      companyName: formValue.companyName,
      registered: true,
    };
    this.registerLoading.set(true);

    this.authService
      .updateUser(updateInviteUser)
      .pipe(
        map((u) => {
          const updatedUser = u.entity as UserBase;
          updatedUser.newPassword = formValue.password;
          return updatedUser;
        }),
        switchMap((u) => this.authService.updatePassword(u)),
        switchMap(() => this.authService.reloadUser()),
      )
      .subscribe({
        next: () => {
          this.router.navigateByUrl("dashboard");
          this.registerLoading.set(false);
        },
        error: (error) => {
          this.toasterService.error(error);
          this.registerLoading.set(false);
        },
      });
  }
}
